'use client';
// Whitepaper — full documentation of the AmanChain architecture
import {
  ShieldCheck, FileText, Scale, Coins, Flame, Network, GitBranch, Map, AlertTriangle, Atom, Gem,
} from 'lucide-react';
import { SectionHeader } from './shared';
import type { NetStats } from './types';
import { fmtAmt } from './shared';

export function Whitepaper({ stats }: { stats: NetStats | null }) {
  return (
    <div className="space-y-6 max-w-4xl mx-auto">
      <SectionHeader icon={FileText} title="Whitepaper — AmanChain v3.0 (Dual Network + AMT-20)" desc="The complete architecture of the platform and the economy of the native AMAN coin" />

      {/* Executive summary */}
      <section className="glass p-6">
        <h3 className="text-lg font-black mb-3 flex items-center gap-2"><ShieldCheck className="size-5 text-primary" /> 1. Executive Summary</h3>
        <div className="space-y-3 text-sm leading-relaxed text-muted-foreground">
          <p>
            <b className="text-foreground">AmanChain</b> is a next-generation blockchain platform operating with its native coin
            <b className="text-emerald-400"> AMAN</b>, engineered from first principles around a single question: what should a
            ledger protect, and for how long? Its defining property is a <b className="text-cyan-300">mandatory hybrid quantum shield</b>
            at the heart of consensus: every transaction carries two required signatures — classic Ed25519 and the lattice-based
            <b className="text-cyan-300"> ML-DSA-87</b> from the NIST FIPS 204 post-quantum standard — and the node structurally
            rejects any transaction missing either one.
          </p>
          <p>
            Everything is built on modern primitives: <b className="text-foreground">SHA3-256 (Keccak)</b> block hashing and
            <b className="text-foreground"> SHA3-512</b> address derivation, with wallets encrypted by default under
            <b className="text-foreground"> AES-256-GCM</b> and an <b className="text-foreground">scrypt</b> key derivation.
            The economy is driven by strict scarcity: a <b className="text-foreground">100 million AMAN</b> hard cap, rewards that
            halve every 50,000 blocks, and a <b className="text-foreground">permanent burn</b> of 50% of every transaction fee and
            100% of every token deployment fee — growing usage pushes supply downward. Beyond transfers, the platform ships with
            the <b className="text-foreground">AMT-20 native token standard</b>, <b className="text-foreground">protocol-level staking</b>
            up to 15% APR with time locks, and a complete live market.
          </p>
          <p>
            The network runs in two parallel instances — <b className="text-foreground">Mainnet</b> (Chain-ID 1) as the permanent
            ledger of record and <b className="text-foreground">Testnet</b> (Chain-ID 2) as an identical-protocol rehearsal space —
            each launched by its operator through a founder wallet whose genesis allocation starts the chain.
          </p>
        </div>
      </section>

      {/* The design problem */}
      <section className="glass p-6">
        <h3 className="text-lg font-black mb-3 flex items-center gap-2"><AlertTriangle className="size-5 text-amber-400" /> 2. The Design Problem: Inherited Weak Points</h3>
        <div className="space-y-3 text-sm leading-relaxed text-muted-foreground">
          <p>
            <b className="text-foreground">The nonce fragility of ECDSA:</b> signatures in the classical scheme depend on a unique
            random value per signature; reusing it, or leaking a fragment of it, reveals the entire private key — a failure class
            that has destroyed real wallets historically. Ed25519 eliminates this class entirely: signatures are deterministic,
            derived from the key and the message, so there is no random nonce to corrupt.
          </p>
          <p>
            <b className="text-foreground">The SHA-2 attack surface:</b> the SHA-2 family, whatever the output length, remains
            structurally exposed to length-extension attacks — an entire genre of security workarounds exists for protocols
            consuming raw SHA-2. SHA3-256&apos;s internal sponge construction (Keccak) prevents this class by design, not by patching.
          </p>
          <p>
            <b className="text-foreground">Exposed key material:</b> wallet files with lightly encrypted or plaintext keys have
            caused major thefts across the industry. In AmanChain no key is ever stored unencrypted: the seed is encrypted with
            AES-256-GCM under a key derived from the owner&apos;s password via scrypt (deliberately expensive brute forcing), and
            GCM authentication makes decryption under a wrong password mathematically impossible.
          </p>
          <p>
            <b className="text-foreground">Single-purpose economies:</b> a chain limited to transfers offers holders no
            participation, no yield, and no native asset layer. AmanChain addresses this with consensus-level tokens, tiered
            staking from 6% to 15% APR with time locks, a live market layer, and a 12-second target block time.
          </p>
          <p>
            <b className="text-cyan-300">The largest incoming threat — quantum:</b> elliptic-curve signatures fall to Shor&apos;s
            algorithm once quantum machines approach published resource estimates; every prior transaction that revealed a public
            key becomes a standing target for &quot;harvest now, decrypt later&quot;. Most existing ledgers have no deployed answer.
            AmanChain settled the question at block zero: a mandatory dual shield.
          </p>
        </div>
      </section>

      {/* Architecture by the numbers */}
      <section className="glass p-6">
        <h3 className="text-lg font-black mb-4 flex items-center gap-2"><Scale className="size-5 text-primary" /> 3. The Architecture, by the Numbers</h3>
        <div className="overflow-x-auto rounded-lg ring-1 ring-border">
          <table className="w-full text-xs md:text-sm">
            <thead>
              <tr className="bg-muted/60">
                <th className="text-left p-3 font-bold">Layer</th>
                <th className="text-left p-3 font-bold text-emerald-400">AmanChain design</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border/60">
              {[
                ['Digital signature', 'Hybrid shield: Ed25519 + ML-DSA-87 (FIPS 204) on every transaction — both must verify'],
                ['Quantum resistance', 'NIST Category 5 (the highest) — deployed, mandatory and enforced by consensus'],
                ['Hash function', 'SHA3-256 (structurally immune to length extension) + SHA3-512 for addresses — 256-bit margin under Grover'],
                ['Key security', 'Mandatory AES-256-GCM + scrypt N=16384 key derivation'],
                ['Replay protection', 'Chain-ID embedded inside both signatures'],
                ['Block time', '12-second target with adaptive retargeting every 5 blocks'],
                ['Fees', '50% permanently burned — permanent deflationary pressure'],
                ['Token standard', 'AMT-20 native in consensus: caps, minting and burning enforced by the protocol'],
                ['Staking & yield', 'Tiered plans at 6%/9%/15% APR with time locks, accruing every second from the protocol'],
                ['Supply cap', '100 million with continuous burns'],
                ['Block reward', '40 AMAN, halving every 50,000 blocks'],
              ].map(([k, a]) => (
                <tr key={k} className="hover:bg-muted/30 transition">
                  <td className="p-3 font-bold whitespace-nowrap">{k}</td>
                  <td className="p-3 text-emerald-300/90">{a}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {/* Cryptographic layer */}
      <section className="glass p-6">
        <h3 className="text-lg font-black mb-3 flex items-center gap-2"><ShieldCheck className="size-5 text-primary" /> 4. The Cryptographic Layer in Detail</h3>
        <div className="space-y-3 text-sm leading-relaxed text-muted-foreground">
          <p>
            <b className="text-foreground">Addresses:</b> the address binds both public keys together: SHA3-512 over
            (classic key || quantum key), truncated to a 20-byte body plus an 8-hex checksum with a domain-separated prefix.
            The final format is <span className="mono text-foreground">AM + body(40) + checksum(8)</span> — 50 characters with
            instant integrity validation before any send. Because the quantum key participates in derivation, the shield
            cannot be stripped from an address.
          </p>
          <p>
            <b className="text-foreground">Signing:</b> every transaction is serialized into a strict canonical string —
            <span className="mono text-foreground"> AMAN-v2 | Chain-ID | type | from | to | amount | fee | nonce | timestamp | data</span> —
            then signed twice over the same message. Verification re-derives the address from the attached keys and checks both
            signatures: complete impersonation prevention.
          </p>
          <p>
            <b className="text-foreground">Wallets:</b> the master seed (32 bytes) derives both keypairs deterministically.
            It is hardened via scrypt under the owner&apos;s password and encrypted with AES-256-GCM, a unique IV per wallet and an
            authentication tag — flipping a single byte of the encrypted file voids decryption entirely.
          </p>
          <p>
            <b className="text-foreground">Merkle tree:</b> each block&apos;s transactions are summarized in a merkle root
            (repeated SHA3-256 over pairs) — modifying one transaction changes the root and collapses the whole proof-of-work.
          </p>
        </div>
      </section>

      {/* Quantum shield */}
      <section className="glass p-6 border-cyan-500/30" style={{ borderStyle: 'solid', borderWidth: 1 }}>
        <h3 className="text-lg font-black mb-3 flex items-center gap-2"><Atom className="size-5 text-cyan-300" /> 5. The Hybrid Quantum Shield — The Defining Chapter</h3>
        <div className="space-y-3 text-sm leading-relaxed text-muted-foreground">
          <p>
            <b className="text-foreground">The problem:</b> the security of today&apos;s pre-quantum ledgers rests on
            elliptic-curve signatures. Shor&apos;s algorithm turns private-key extraction from a public key from an infeasible
            search into an efficient algebraic procedure. Worse: past transactions already expose public keys, so the attack
            can be harvested today and executed the day suitable hardware exists.
          </p>
          <p>
            <b className="text-foreground">The AmanChain answer — mandatory dual signing:</b> every transaction carries a classic
            signature (Ed25519, 64 bytes) and a lattice signature (ML-DSA-87, 4,627 bytes) over the same canonical message. The
            v3 consensus rule rejects any transaction missing or failing either signature. A quantum attacker cannot exploit the
            classic layer because the transaction only enters the chain with an intact lattice signature they cannot forge — and
            no efficient quantum attack is known against lattice schemes at these parameters.
          </p>
          <p>
            <b className="text-foreground">Why ML-DSA-87 specifically?</b> It is the highest level (Category 5, 256-bit security)
            of the <b className="text-cyan-300">NIST FIPS 204</b> standard published in August 2024 — the outcome of an eight-year
            global competition whose candidates endured open analysis from the world&apos;s best laboratories. The same standard
            governments are adopting to protect state secrets. We deliberately rejected the lower variants (44/65): for
            long-horizon money, excess margin is not a luxury.
          </p>
          <p>
            <b className="text-foreground">The entire network is quantum-shielded from block zero:</b> since every transaction
            from day one carries the shield, there is no &quot;legacy unprotected period&quot; whose keys can be harvested — unlike
            any ledger planning a future migration, whose entire historical record remains a target.
          </p>
        </div>
      </section>

      {/* Consensus */}
      <section className="glass p-6">
        <h3 className="text-lg font-black mb-3 flex items-center gap-2"><Network className="size-5 text-sky-400" /> 6. Consensus: Q-PoW and Adaptive Difficulty</h3>
        <div className="space-y-3 text-sm leading-relaxed text-muted-foreground">
          <p>
            The chain runs real proof-of-work: the miner searches for a nonce making
            <span className="mono text-foreground"> SHA3-256(block header) </span>
            start with a number of hex zeros equal to the current difficulty. Every extra zero multiplies the required work by
            16, so the cost of rewriting history escalates exponentially with chain length.
          </p>
          <p>
            <b className="text-foreground">Adaptive retargeting:</b> every 5 blocks the actual time window is measured against
            the 12-second target and difficulty adjusts by the deviation ratio within bounded step guards — keeping the network
            regular as miner power changes, and never trapping difficulty above the network&apos;s sustainable capacity.
          </p>
          <p>
            <b className="text-foreground">Replay protection:</b> because the Chain-ID is part of the signed message, a valid
            Mainnet transaction is automatically rejected on Testnet and vice versa — even with identical keys.
          </p>
        </div>
      </section>

      {/* Tokenomics */}
      <section className="glass p-6">
        <h3 className="text-lg font-black mb-3 flex items-center gap-2"><Coins className="size-5 text-amber-400" /> 7. The AMAN Economy</h3>
        <div className="grid sm:grid-cols-4 gap-3 mb-4">
          {[
            ['Supply cap', '100,000,000'],
            ['Founder allocation', '20,000,000'],
            ['Block reward', `${fmtAmt(stats?.blockReward ?? 40)} AMAN`],
            ['Halving', 'every 50,000 blocks'],
          ].map(([k, v]) => (
            <div key={k} className="rounded-lg bg-muted/40 ring-1 ring-border p-3 text-center">
              <div className="text-[0.62rem] text-muted-foreground">{k}</div>
              <div className="text-sm font-black mt-1" dir="ltr">{v}</div>
            </div>
          ))}
        </div>
        <div className="space-y-3 text-sm leading-relaxed text-muted-foreground">
          <p>
            <b className="text-foreground">Distribution:</b> 20% of the cap is minted once at genesis to the founder wallet that
            launches the network — the operator&apos;s own starting position, visible on-chain from block zero. The remaining 80%
            is distributed exclusively through mining over decades of halvings. No stealth pre-mines beyond the public genesis
            allocation; everything is verifiable in the Explorer.
          </p>
          <p>
            <b className="text-foreground">The deflationary burn:</b> every transaction burns half its fee and every token
            deployment burns its entire fee — burned coins (tracked live:
            <span className="mono text-rose-400"> {fmtAmt(stats?.totalBurned ?? 0)} AMAN</span>) are permanently removed from
            supply, turning usage itself into a driving force of scarcity.
          </p>
          <p>
            <b className="text-foreground">Real value creation:</b> AMAN&apos;s value rests on three pillars: (1) programmed
            scarcity with permanent burns, (2) mandatory operational utility — every token operation and transfer pays its fee
            in AMAN, (3) staking that locks circulating supply and rewards long-term commitment with plans up to 15% APR from
            the network protocol.
          </p>
        </div>
      </section>

      {/* Token platform */}
      <section className="glass p-6">
        <h3 className="text-lg font-black mb-3 flex items-center gap-2"><Gem className="size-5 text-violet-400" /> 8. The AMT-20 Token Platform</h3>
        <div className="space-y-3 text-sm leading-relaxed text-muted-foreground">
          <p>
            Any wallet can deploy a token directly in the consensus layer: a unique symbol (2-8 uppercase characters), a name,
            decimals (0-8), a hard max supply and an initial supply minted to the creator. Deployment burns a 25 AMAN fee —
            filtering serious projects and feeding the native economy. Tokens are objects of chain state, transferred with the
            same dual-signed security as the coin itself — no external smart contracts, no extra virtual machine, no new
            vulnerability surface.
          </p>
          <p>
            Minting is reserved for the token creator and can never exceed the declared max supply; any holder may burn their
            own holdings, reducing supply permanently. The same symbol may exist independently on Mainnet and Testnet — teams
            rehearse a full deployment on Testnet (with its faucet) before committing to Mainnet. Use cases span community
            currencies, loyalty points, receipts, credentials and tradable rewards — all inheriting the quantum shield
            automatically.
          </p>
        </div>
      </section>

      {/* Roadmap */}
      <section className="glass p-6">
        <h3 className="text-lg font-black mb-3 flex items-center gap-2"><Map className="size-5 text-primary" /> 9. Roadmap</h3>
        <div className="space-y-2.5 text-sm">
          {[
            ['Phase 1 — delivered', 'The hybrid cryptographic core, the ML-DSA-87 quantum shield, Q-PoW consensus, encrypted wallets, mining, explorer, tiered staking', true],
            ['Phase 2 — delivered', 'Live AMAN/USD market with candles and order book, paper trading with P&L, investor hub with tokenomics and institutional readiness', true],
            ['Phase 3 — delivered', 'Dual-network architecture (Mainnet + Testnet), the AMT-20 token platform with creator-controlled minting, user-driven network launch', true],
            ['Phase 4 — next', 'Multi-node P2P synchronization, institutional custody, KYC/AML gateway, ISO 20022 messaging, on-chain governance', false],
          ].map(([phase, desc, done]) => (
            <div key={phase as string} className="flex items-start gap-3 rounded-lg border border-border/60 bg-muted/30 p-3.5">
              <GitBranch className={`size-4 mt-0.5 shrink-0 ${done ? 'text-emerald-400' : 'text-muted-foreground'}`} />
              <div>
                <div className={`font-bold text-xs ${done ? 'text-emerald-400' : ''}`}>{phase as string}</div>
                <div className="text-xs text-muted-foreground mt-0.5">{desc as string}</div>
              </div>
            </div>
          ))}
        </div>
      </section>

      <div className="glass p-5 border-amber-500/30" style={{ borderStyle: 'solid', borderWidth: 1 }}>
        <div className="flex items-start gap-3">
          <AlertTriangle className="size-5 text-amber-400 shrink-0 mt-0.5" />
          <p className="text-xs text-muted-foreground leading-relaxed">
            <b className="text-foreground">Disclosure:</b> AmanChain here is a working prototype — the cryptography, consensus and
            economics are real and fully operational inside this platform, but it runs as a demonstration node for research and
            education, and its assets carry demonstration value only. Operating a production network requires node distribution,
            external security audits and community governance per the roadmap.
          </p>
        </div>
      </div>
    </div>
  );
}
